/**
 * Subir el extracto del banco.
 *
 * NO VA CONTRA EL MES QUE SE ESTÁ MIRANDO. Cada movimiento cae en el mes de su fecha, decida lo
 * que decida la pantalla: si estando en septiembre se sube el extracto de agosto, los
 * movimientos van a agosto, y la pantalla salta a agosto para enseñarlos. Imputarlos al mes
 * abierto habría puesto los cobros de agosto en septiembre.
 *
 * EL RESUMEN VA EN EL AVISO, no en un diálogo: lo que importa —cuántos cobros se han
 * identificado y cuántos no— se ve a continuación en el propio bloque del extracto, que es
 * donde se resuelven.
 *
 * LO QUE LAS REGLAS DEJAN PENDIENTE LO COGE LA IA en segundo plano (`aiRun`): el aviso lo dice, y
 * la franja de encima de la tabla enseña cómo va y, al acabar, lo que ha resuelto y sus dudas.
 */
import { useRef, useState } from 'react';
import type { StatementImportResult } from '@naar/shared';
import { MONTH_NAMES_ES } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { showToast, toast } from '../../components/feedback/Toaster';
import styles from './Ledger.module.css';

interface StatementUploadProps {
  /** Mes que se está mirando, para no saltar si los movimientos caen en él. */
  month: string;
  onImported: (result: StatementImportResult) => void | Promise<void>;
  /** Para ir al mes del extracto cuando no es el que se está mirando. */
  onGoToMonth: (month: string) => void;
}

function nombreDeMes(month: string): string {
  return `${MONTH_NAMES_ES[Number(month.slice(5, 7)) - 1]!.toLowerCase()} de ${month.slice(0, 4)}`;
}

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`;
}

export function StatementUpload({ month, onImported, onGoToMonth }: StatementUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [progreso, setProgreso] = useState<number | null>(null);

  async function subir(file: File | undefined) {
    if (!file) return;
    setProgreso(0);
    try {
      const result = await api.uploadStatement(file, setProgreso);
      await onImported(result);

      if (result.imported === 0) {
        toast.info('Ese extracto no trae movimientos que calcular: lo que tiene ya estaba resuelto a mano.');
      } else {
        const partes = [
          plural(result.identifiedIncome, 'cobro identificado', 'cobros identificados'),
          result.pendingIncome > 0 ? plural(result.pendingIncome, 'ingreso sin identificar', 'ingresos sin identificar') : null,
          result.linkedExpenses > 0
            ? plural(result.linkedExpenses, 'cargo enlazado con su ticket', 'cargos enlazados con su ticket')
            : null,
          result.pendingExpenses > 0 ? plural(result.pendingExpenses, 'cargo por justificar', 'cargos por justificar') : null,
        ].filter(Boolean);
        // El último extracto manda en sus fechas: si había otro, se dice que lo ha sustituido.
        const sustituye =
          result.replaced > 0
            ? ` Sustituye a lo subido antes en esas fechas (${plural(result.replaced, 'movimiento', 'movimientos')}), y todo se ha calculado de nuevo${result.duplicates > 0 ? `; ${plural(result.duplicates, 'movimiento resuelto a mano se conserva', 'movimientos resueltos a mano se conservan')}` : ''}.`
            : '';
        const ia = result.aiRun
          ? ` La IA está revisando ${result.aiRun.total === 1 ? 'el movimiento pendiente' : `los ${result.aiRun.total} movimientos pendientes`} con toda la información de la cartera: tarda unos minutos y te aviso al terminar.`
          : '';
        toast.success(
          `${plural(result.imported, 'movimiento importado', 'movimientos importados')} de ${result.months.map(nombreDeMes).join(', ')}: ${partes.join(', ')}.${sustituye}${ia}`,
        );
      }
      /*
       * El aviso de que el saldo no encadena dura bastante más que un aviso normal: dice que
       * pueden faltar movimientos, y en cuatro segundos no da tiempo ni a leerlo.
       */
      for (const aviso of result.warnings) showToast(aviso, { tone: 'warning', durationMs: 15_000 });

      const destino = result.months.includes(month) ? null : result.months[result.months.length - 1];
      if (destino) onGoToMonth(destino);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido subir el extracto.');
    } finally {
      setProgreso(null);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className={styles.dropInput}
        onChange={(event) => {
          void subir(event.target.files?.[0]);
          // Se limpia para que volver a elegir el MISMO fichero dispare el evento otra vez.
          event.target.value = '';
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={progreso !== null}
      >
        <Icon name="upload" size={15} strokeWidth={2} />
        {progreso === null ? 'Subir extracto' : progreso < 100 ? `Subiendo… ${progreso} %` : 'Leyendo el extracto…'}
      </Button>
    </>
  );
}
